import { X } from 'lucide-react';
import type {
  InventoryFacetKey,
  InventoryFacets,
  InventoryFmv,
} from '../../../lib/api/operatorConsole';
import { Checkbox } from '../../../components/ui/Checkbox';
import { FacetGroup } from '../../../components/ui/FacetGroup';
import { Select } from '../../../components/ui/Select';
import {
  EMPTY_INVENTORY_FILTERS,
  OWNERSHIP_OPTIONS,
  asOwnership,
  hasActiveFilters,
  type InventoryFilterValues,
} from './inventoryFilters';

interface Props {
  filters: InventoryFilterValues;
  /** Facets of the current result set (null while nothing has loaded). */
  facets: InventoryFacets | null;
  fmv: InventoryFmv | null;
  onChange: (next: InventoryFilterValues) => void;
}

const LEGEND = 'mb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--co-muted)]';
const NUMBER =
  'w-full rounded-md border border-[var(--co-border)] bg-[var(--co-surface)] px-2 py-1 text-[12px] text-[var(--co-text)] outline-none focus:border-[var(--co-brand)]';

/**
 * Left column of the add-cards picker. Facet counts come back with every search
 * (post-filter aggregations), so ticking one narrows the others. There is no
 * membership control: the picker only ever shows cards outside the pack.
 */
export function FacetPanel({ filters, facets, fmv, onChange }: Props) {
  function set<K extends keyof InventoryFilterValues>(key: K, value: InventoryFilterValues[K]) {
    onChange({ ...filters, [key]: value });
  }

  const facetOf = (key: InventoryFacetKey) => facets?.[key] ?? [];
  // Single select: '' = every category. A category picked before the facets
  // (re)load stays visible as a bare id so the select never shows a value it
  // has no option for.
  const categoryOptions = [
    { value: '', label: 'All categories' },
    ...facetOf('category').map((f) => ({ value: String(f.id), label: `${f.label} (${f.count})` })),
  ];
  if (filters.category_id && !categoryOptions.some((o) => o.value === filters.category_id)) {
    categoryOptions.push({ value: filters.category_id, label: `#${filters.category_id}` });
  }
  const valueRange =
    fmv && fmv.min !== null && fmv.max !== null ? ` (${fmv.min} – ${fmv.max})` : '';

  return (
    <aside
      aria-label="Inventory filters"
      className="space-y-4 rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-3"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-[13px] font-semibold text-[var(--co-text)]">Filters</h2>
        {hasActiveFilters(filters) && (
          <button
            type="button"
            onClick={() => onChange({ ...EMPTY_INVENTORY_FILTERS, sort: filters.sort })}
            className="flex items-center gap-1 text-[11px] text-[var(--co-muted)] hover:text-[var(--co-text)]"
          >
            <X size={12} aria-hidden="true" />
            Reset
          </button>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <span className={LEGEND}>Ownership</span>
        <Select
          aria-label="Ownership"
          value={filters.ownership}
          options={OWNERSHIP_OPTIONS}
          onChange={(v) => set('ownership', asOwnership(v))}
        />
      </div>

      <fieldset className="border-0 p-0">
        <legend className={LEGEND}>{`Value${valueRange}`}</legend>
        <div className="flex items-center gap-2">
          <input
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            aria-label="Min value"
            placeholder="min"
            value={filters.fmv_min}
            onChange={(e) => set('fmv_min', e.target.value)}
            className={NUMBER}
          />
          <span className="text-[11px] text-[var(--co-muted)]">–</span>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            aria-label="Max value"
            placeholder="max"
            value={filters.fmv_max}
            onChange={(e) => set('fmv_max', e.target.value)}
            className={NUMBER}
          />
        </div>
      </fieldset>

      <div className="flex flex-col gap-1">
        <span className={LEGEND}>Category</span>
        <Select
          aria-label="Category"
          value={filters.category_id}
          options={categoryOptions}
          onChange={(v) => set('category_id', v)}
        />
      </div>

      <FacetGroup
        title="Sport"
        options={facetOf('sport')}
        selected={filters.sport}
        onChange={(v) => set('sport', v)}
      />
      <FacetGroup
        title="Grader"
        options={facetOf('grader')}
        selected={filters.grader}
        onChange={(v) => set('grader', v)}
      />
      <FacetGroup
        title="Grade"
        options={facetOf('grade')}
        selected={filters.grade}
        onChange={(v) => set('grade', v)}
      />

      <fieldset className="border-0 p-0">
        <legend className={LEGEND}>Year</legend>
        <div className="flex items-center gap-2">
          <input
            type="number"
            inputMode="numeric"
            aria-label="Year from"
            placeholder="from"
            value={filters.year_from}
            onChange={(e) => set('year_from', e.target.value)}
            className={NUMBER}
          />
          <span className="text-[11px] text-[var(--co-muted)]">–</span>
          <input
            type="number"
            inputMode="numeric"
            aria-label="Year to"
            placeholder="to"
            value={filters.year_to}
            onChange={(e) => set('year_to', e.target.value)}
            className={NUMBER}
          />
        </div>
      </fieldset>

      <FacetGroup
        title="Set"
        options={facetOf('set')}
        selected={filters.set}
        onChange={(v) => set('set', v)}
      />

      <Checkbox
        aria-label="Rookie cards only"
        label="Rookie cards only"
        checked={filters.rookie}
        onChange={(v) => set('rookie', v)}
      />

      <FacetGroup
        title="Pokémon rarity"
        options={facetOf('pokemon_rarity')}
        selected={filters.pokemon_rarity}
        onChange={(v) => set('pokemon_rarity', v)}
      />
    </aside>
  );
}
